import { db, DexieTemplate, DexieSignedContract } from './dexieDb';
import { ContractTemplate, SignedContract, AppSettings, Language } from '@/shared/types';

// ============================================
// PROFILE / SETTINGS
// ============================================

export async function getProfileOffline(userId: string) {
  return db.profiles.get(userId);
}

export async function updateProfileOffline(userId: string, updates: Partial<{
  display_name: string;
  business_name: string;
  email: string;
  default_language: string;
  pin_code: string;
  pin_enabled: boolean;
  logo_url: string | null;
  first_name: string;
  last_name: string;
  phone: string;
  instagram: string;
  business_address: Record<string, string>;
}>) {
  const existing = await db.profiles.get(userId);
  if (existing) {
    await db.profiles.update(userId, {
      ...updates,
      _syncStatus: 'pending_update',
      _lastModified: Date.now(),
    });
  }
}

export function profileToSettings(profile: any): AppSettings {
  return {
    defaultLanguage: (profile?.default_language || 'fr') as Language,
    practitionerEmail: profile?.email || '',
    practitionerName: profile?.display_name || '',
    businessName: profile?.business_name || '',
    pinCode: profile?.pin_code || '1234',
    pinEnabled: profile?.pin_enabled || false,
    logoUrl: profile?.logo_url || null,
  };
}

// ============================================
// TEMPLATES
// ============================================

function dexieToTemplate(row: DexieTemplate): ContractTemplate {
  return {
    id: row.id,
    name: row.name as Record<Language, string>,
    category: row.category,
    description: row.description as Record<Language, string>,
    icon: row.icon,
    sections: row.sections,
    fields: row.fields,
    checkboxes: row.checkboxes,
    tags: row.tags || [],
    archived: row.archived,
    is_system: row.is_system ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function getTemplatesOffline(userId: string): Promise<ContractTemplate[]> {
  const rows = await db.templates
    .where('owner_id').equals(userId)
    .and(t => t._syncStatus !== 'pending_delete')
    .toArray();

  if (rows.length === 0) return [];

  return rows.map(dexieToTemplate);
}

export async function saveTemplateOffline(userId: string, template: ContractTemplate): Promise<void> {
  const existing = await db.templates.get(template.id);
  const status = existing ? (existing._syncStatus === 'pending_create' ? 'pending_create' : 'pending_update') : 'pending_create';

  await db.templates.put({
    id: template.id,
    owner_id: userId,
    name: template.name,
    category: template.category,
    description: template.description,
    icon: template.icon,
    sections: template.sections,
    fields: template.fields,
    checkboxes: template.checkboxes,
    tags: template.tags,
    archived: template.archived,
    created_at: template.createdAt,
    updated_at: new Date().toISOString(),
    _syncStatus: status,
    _lastModified: Date.now(),
  });
}

export async function deleteTemplateOffline(userId: string, id: string): Promise<void> {
  const existing = await db.templates.get(id);
  if (!existing) return;

  if (existing._syncStatus === 'pending_create') {
    // Never synced — just delete locally
    await db.templates.delete(id);
  } else {
    await db.templates.update(id, {
      _syncStatus: 'pending_delete',
      _lastModified: Date.now(),
    });
  }
}

// ============================================
// SIGNED CONTRACTS
// ============================================

function dexieToSignedContract(row: DexieSignedContract): SignedContract {
  return {
    id: row.id,
    templateId: row.template_id || '',
    templateName: row.template_name,
    clientId: row.client_id || null,
    clientName: row.client_name,
    clientEmail: row.client_email || '',
    language: row.language as Language,
    fieldValues: row.field_values,
    checkboxValues: row.checkbox_values,
    signatureDataUrl: row.signature_data_url || '',
    signedAt: row.signed_at,
    practitionerEmail: row.practitioner_email || '',
  };
}

export async function getSignedContractsOffline(userId: string): Promise<SignedContract[]> {
  const rows = await db.signedContracts
    .where('owner_id').equals(userId)
    .and(c => c._syncStatus !== 'pending_delete')
    .reverse()
    .sortBy('signed_at');
  return rows.map(dexieToSignedContract);
}

export async function saveSignedContractOffline(userId: string, contract: SignedContract): Promise<void> {
  await db.signedContracts.put({
    id: contract.id,
    owner_id: userId,
    template_id: contract.templateId || null,
    template_name: contract.templateName,
    client_id: contract.clientId || null,
    client_name: contract.clientName,
    client_email: contract.clientEmail,
    language: contract.language,
    field_values: contract.fieldValues,
    checkbox_values: contract.checkboxValues,
    signature_data_url: contract.signatureDataUrl,
    signed_at: contract.signedAt,
    practitioner_email: contract.practitionerEmail,
    _syncStatus: 'pending_create',
    _lastModified: Date.now(),
  });
}

export async function deleteSignedContractOffline(userId: string, id: string): Promise<void> {
  const existing = await db.signedContracts.get(id);
  if (!existing) return;

  if (existing._syncStatus === 'pending_create') {
    await db.signedContracts.delete(id);
  } else {
    await db.signedContracts.update(id, {
      _syncStatus: 'pending_delete',
      _lastModified: Date.now(),
    });
  }
}
